import { supabase } from '../lib/supabase';

/**
 * Professional Client Management Service
 * Handles all professional client operations for admin dashboard
 */

export const professionalClientService = {
  /**
   * Get all professional clients with stats
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} List of professional clients
   */
  async getAllClients({ searchTerm = '', clientType = '', status = '', sortBy = 'created_at', sortOrder = 'desc' } = {}) {
    try {
      let query = supabase?.from('professional_clients')?.select('*');

      // Apply filters
      if (searchTerm) {
        query = query?.or(`company_name.ilike.%${searchTerm}%,contact_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      if (clientType) {
        query = query?.eq('client_type', clientType);
      }

      if (status) {
        query = query?.eq('account_status', status);
      }

      // Apply sorting
      query = query?.order(sortBy, { ascending: sortOrder === 'asc' });

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching professional clients:', error?.message, error?.code);
        throw error;
      }

      // Convert snake_case to camelCase
      return (data || [])?.map(client => ({
        id: client?.id,
        authUserId: client?.auth_user_id,
        companyName: client?.company_name,
        contactName: client?.contact_name,
        email: client?.email,
        phone: client?.phone,
        clientType: client?.client_type,
        siret: client?.siret,
        address: client?.address,
        assignedAdvisor: client?.assigned_advisor,
        dedicatedLineNumber: client?.dedicated_line_number,
        negotiatedRate: client?.negotiated_rate,
        pickupAddresses: client?.pickup_addresses || [],
        internalNotes: client?.internal_notes,
        totalShipments: client?.total_shipments || 0,
        totalRevenue: client?.total_revenue || 0,
        accountStatus: client?.account_status,
        isActive: client?.is_active,
        createdAt: client?.created_at,
        updatedAt: client?.updated_at
      }));
    } catch (error) {
      console.error('Error in getAllClients:', error);
      throw error;
    }
  },

  /**
   * Get single professional client by ID
   * @param {string} clientId - Client UUID
   * @returns {Promise<Object>} Client details
   */
  async getClientById(clientId) {
    try {
      const { data, error } = await supabase?.from('professional_clients')?.select('*')?.eq('id', clientId)?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching client:', error?.message);
        return null;
      }

      if (!data) return null;

      return {
        id: data?.id,
        authUserId: data?.auth_user_id,
        companyName: data?.company_name,
        contactName: data?.contact_name,
        email: data?.email,
        phone: data?.phone,
        clientType: data?.client_type,
        siret: data?.siret,
        address: data?.address,
        assignedAdvisor: data?.assigned_advisor,
        dedicatedLineNumber: data?.dedicated_line_number,
        negotiatedRate: data?.negotiated_rate,
        pickupAddresses: data?.pickup_addresses || [],
        internalNotes: data?.internal_notes,
        totalShipments: data?.total_shipments || 0,
        totalRevenue: data?.total_revenue || 0,
        accountStatus: data?.account_status,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error in getClientById:', error);
      throw error;
    }
  },

  /**
   * Update professional client information
   * @param {string} clientId - Client UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated client
   */
  async updateClient(clientId, updates) {
    try {
      const updateData = {
        company_name: updates?.companyName,
        contact_name: updates?.contactName,
        email: updates?.email,
        phone: updates?.phone,
        client_type: updates?.clientType,
        siret: updates?.siret,
        address: updates?.address,
        assigned_advisor: updates?.assignedAdvisor,
        dedicated_line_number: updates?.dedicatedLineNumber,
        negotiated_rate: updates?.negotiatedRate,
        pickup_addresses: updates?.pickupAddresses,
        internal_notes: updates?.internalNotes,
        account_status: updates?.accountStatus,
        is_active: updates?.isActive,
        updated_at: new Date()?.toISOString()
      };

      // Remove undefined values
      Object.keys(updateData)?.forEach(key => {
        if (updateData?.[key] === undefined) {
          delete updateData?.[key];
        }
      });

      const { data, error } = await supabase?.from('professional_clients')?.update(updateData)?.eq('id', clientId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error updating client:', error?.message);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in updateClient:', error);
      throw error;
    }
  },

  /**
   * Get client shipments
   * @param {string} clientId - Client UUID
   * @returns {Promise<Array>} List of shipments
   */
  async getClientShipments(clientId) {
    try {
      // Get client info first
      const client = await this.getClientById(clientId);
      if (!client) return [];

      // Query shipments by phone
      const { data, error } = await supabase?.from('shipments')?.select('*')?.eq('sender_phone', client?.phone)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching shipments:', error?.message);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getClientShipments:', error);
      throw error;
    }
  },

  /**
   * Get client documents
   * @param {string} clientId - Client UUID
   * @param {string} documentType - Filter by document type
   * @returns {Promise<Array>} List of documents
   */
  async getClientDocuments(clientId, documentType = null) {
    try {
      let query = supabase?.from('pro_documents')?.select('*')?.eq('professional_client_id', clientId)?.order('uploaded_at', { ascending: false });

      if (documentType) {
        query = query?.eq('document_type', documentType);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching documents:', error?.message);
        return [];
      }

      return (data || [])?.map(doc => ({
        id: doc?.id,
        professionalClientId: doc?.professional_client_id,
        shipmentId: doc?.shipment_id,
        documentType: doc?.document_type,
        fileUrl: doc?.file_url,
        fileName: doc?.file_name,
        fileSize: doc?.file_size,
        bucketName: doc?.bucket_name,
        documentNumber: doc?.document_number,
        documentDate: doc?.document_date,
        amountHt: doc?.amount_ht,
        amountTtc: doc?.amount_ttc,
        caption: doc?.caption,
        description: doc?.description,
        status: doc?.status,
        uploadedBy: doc?.uploaded_by,
        uploadedAt: doc?.uploaded_at,
        createdAt: doc?.created_at,
        updatedAt: doc?.updated_at
      }));
    } catch (error) {
      console.error('Error in getClientDocuments:', error);
      throw error;
    }
  },

  /**
   * Upload document for professional client
   * @param {string} clientId - Client UUID
   * @param {File} file - File to upload
   * @param {Object} metadata - Document metadata
   * @returns {Promise<Object>} Created document record
   */
  async uploadDocument(clientId, file, metadata) {
    try {
      // Upload file to storage
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${clientId}/${Date.now()}.${fileExt}`;
      const filePath = `pro-documents/${fileName}`;

      const { error: uploadError } = await supabase?.storage?.from('pro-documents')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError?.message);
        throw new Error(`Failed to upload file: ${uploadError.message}`);
      }

      // Create document record
      const { data, error } = await supabase?.from('pro_documents')?.insert({
          professional_client_id: clientId,
          shipment_id: metadata?.shipmentId || null,
          document_type: metadata?.documentType,
          file_url: filePath,
          file_name: file?.name,
          file_size: file?.size,
          bucket_name: 'pro-documents',
          document_number: metadata?.documentNumber || null,
          document_date: metadata?.documentDate || null,
          amount_ht: metadata?.amountHt || null,
          amount_ttc: metadata?.amountTtc || null,
          caption: metadata?.caption || null,
          description: metadata?.description || null,
          status: metadata?.status || 'pending'
        })?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error creating document record:', error?.message);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in uploadDocument:', error);
      throw error;
    }
  },

  /**
   * Get signed URL for document
   * @param {string} filePath - File path in storage
   * @returns {Promise<string>} Signed URL
   */
  async getDocumentSignedUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage?.from('pro-documents')?.createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) {
        console.error('Error creating signed URL:', error?.message);
        return null;
      }

      return data?.signedUrl || null;
    } catch (error) {
      console.error('Error in getDocumentSignedUrl:', error);
      return null;
    }
  },

  /**
   * Get client communications
   * @param {string} clientId - Client UUID
   * @returns {Promise<Array>} List of communications
   */
  async getClientCommunications(clientId) {
    try {
      const { data, error } = await supabase?.from('pro_communications')?.select('*')?.eq('professional_client_id', clientId)?.order('sent_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching communications:', error?.message);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getClientCommunications:', error);
      throw error;
    }
  },

  /**
   * Create communication record
   * @param {string} clientId - Client UUID
   * @param {Object} communication - Communication details
   * @returns {Promise<Object>} Created communication
   */
  async createCommunication(clientId, communication) {
    try {
      const { data, error } = await supabase?.from('pro_communications')?.insert({
          professional_client_id: clientId,
          communication_type: communication?.type,
          subject: communication?.subject || null,
          message: communication?.message,
          shipment_id: communication?.shipmentId || null,
          document_id: communication?.documentId || null,
          email_status: communication?.emailStatus || null
        })?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error creating communication:', error?.message);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in createCommunication:', error);
      throw error;
    }
  },

  /**
   * Get pending orders for client
   * @param {string} clientId - Client UUID
   * @returns {Promise<Array>} List of pending orders
   */
  async getPendingOrders(clientId = null) {
    try {
      let query = supabase?.from('pro_pending_orders')?.select('*, professional_clients(company_name, contact_name)')?.order('created_at', { ascending: false });

      if (clientId) {
        query = query?.eq('professional_client_id', clientId);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching pending orders:', error?.message);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getPendingOrders:', error);
      throw error;
    }
  },

  /**
   * Get EX1 certificates to process
   * @param {string} status - Filter by status
   * @returns {Promise<Array>} List of EX1 certificates
   */
  async getEX1Certificates(status = null) {
    try {
      // Use the view that includes computed delay fields
      let query = supabase?.from('pro_ex1_tracking_with_delays')?.select('*, professional_clients(company_name, contact_name)')?.order('requested_at', { ascending: false });

      if (status) {
        query = query?.eq('status', status);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching EX1 certificates:', error?.message);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getEX1Certificates:', error);
      throw error;
    }
  },

  /**
   * Update EX1 certificate status
   * @param {string} ex1Id - EX1 tracking UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated EX1 record
   */
  async updateEX1Status(ex1Id, updates) {
    try {
      const { data, error } = await supabase?.from('pro_ex1_tracking')?.update({
          status: updates?.status,
          certificate_number: updates?.certificateNumber,
          stamped_at: updates?.stampedAt,
          delivered_at: updates?.deliveredAt,
          notes: updates?.notes,
          updated_at: new Date()?.toISOString()
        })?.eq('id', ex1Id)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error updating EX1 status:', error?.message);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in updateEX1Status:', error);
      throw error;
    }
  },

  /**
   * Get client analytics/statistics
   * @param {string} clientId - Client UUID
   * @returns {Promise<Object>} Client statistics
   */
  async getClientAnalytics(clientId) {
    try {
      const client = await this.getClientById(clientId);
      const shipments = await this.getClientShipments(clientId);
      const documents = await this.getClientDocuments(clientId);

      // Calculate statistics
      const totalShipments = shipments?.length;
      const completedShipments = shipments?.filter(s => s?.current_status === 'delivered')?.length;
      const inTransitShipments = shipments?.filter(s => s?.current_status === 'in_transit')?.length;
      
      // Calculate monthly shipments
      const monthlyShipments = {};
      shipments?.forEach(shipment => {
        const month = new Date(shipment.created_at)?.toISOString()?.slice(0, 7);
        monthlyShipments[month] = (monthlyShipments?.[month] || 0) + 1;
      });

      return {
        totalShipments,
        completedShipments,
        inTransitShipments,
        totalRevenue: client?.totalRevenue || 0,
        totalDocuments: documents?.length,
        invoicesCount: documents?.filter(d => d?.documentType === 'invoice')?.length,
        ex1Count: documents?.filter(d => d?.documentType === 'ex1_certificate')?.length,
        photosCount: documents?.filter(d => d?.documentType === 'photo')?.length,
        monthlyShipments,
        averageShipmentsPerMonth: totalShipments > 0 ? (totalShipments / Object.keys(monthlyShipments)?.length)?.toFixed(1) : 0
      };
    } catch (error) {
      console.error('Error in getClientAnalytics:', error);
      throw error;
    }
  }
};

/**
 * Check if error is schema-related (should be thrown)
 * @param {Object} error - Supabase error object
 * @returns {boolean} True if schema error
 */
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}